import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Checkbox, Chip, CircularProgress, Collapse,
  Divider, FormControlLabel, LinearProgress, Paper, Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AppShell from './components/AppShell';
import QnrUploadStep from './components/QnrUploadStep';
import api from './services/api';

// ─── Labels ───────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
  // deterministic comparison
  question_missing: 'Not scripted',
  question_extra: 'Not in QNR',
  type_mismatch: 'Wrong question type',
  wording_drift: 'Wording differs from QNR',
  description_drift: 'Sub-text differs from QNR',
  alias_mismatch: 'Alias differs',
  alias_missing: 'Alias missing',
  matched_on_title: 'Matched on wording',
  options_missing: 'No options',
  options_missing_items: 'Options missing',
  options_extra_items: 'Extra options',
  options_order: 'Option order',
  rows_missing: 'No grid rows',
  rows_missing_items: 'Grid rows missing',
  rows_extra_items: 'Extra grid rows',
  rows_order: 'Grid row order',
  duplicate_row: 'Duplicate grid row',
  duplicate_option: 'Duplicate option',
  single_option: 'Only one option',
  doc_duplicate_option: 'Duplicate option in QNR',
  doc_scale_symmetric: 'Symmetric scale in QNR',
  doc_scale_direction: 'Scale reverses in QNR',
  doc_scale_wrong_pole: 'Wrong pole in QNR',
  doc_scale_option_order: 'Option placement in QNR',
  // scale shape
  scale_symmetric: 'Symmetric scale',
  scale_direction: 'Scale reverses',
  scale_wrong_pole: 'Point on the wrong pole',
  scale_option_order: 'Option placement',
  // logic
  logic_display: 'Display logic',
  logic_disqualify: 'Disqualify logic',
  logic_screenout: 'Screenout logic',
};

const SEVERITY_ICONS = {
  error: <ErrorRoundedIcon fontSize="small" color="error" />,
  warning: <WarningAmberRoundedIcon fontSize="small" color="warning" />,
  info: <InfoRoundedIcon fontSize="small" color="info" />,
};

const SEVERITY_COLOURS = { error: 'error', warning: 'warning', info: 'info' };
const SEVERITY_RANK = { error: 0, warning: 1, info: 2 };

const STAGE_LABELS = {
  queued: 'Waiting for a worker…',
  fetching: 'Reading the live survey from Alchemer…',
  checking: 'Comparing every question against the questionnaire…',
};

// ─── Tick state, kept per survey so it survives a reload ──────────────────────

const tickKey = (surveyId) => `surveyTest:ticked:${surveyId}`;

const loadTicked = (surveyId) => {
  try {
    const raw = localStorage.getItem(tickKey(surveyId));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const saveTicked = (surveyId, ticked) => {
  try {
    localStorage.setItem(tickKey(surveyId), JSON.stringify([...ticked]));
  } catch {
    /* private browsing or blocked storage — ticks just won't persist */
  }
};

// ─── Step 2: confirm what was parsed ─────────────────────────────────────────

const ConfirmStep = ({ questions, qaIssues, onRun, onReset }) => (
  <Box>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
        Ready to test
      </Typography>
      <Button size="small" onClick={onReset}>Upload different file</Button>
    </Box>

    <Typography variant="body2" color="textSecondary" mb={3}>
      {questions.length} question{questions.length !== 1 ? 's' : ''} read from the questionnaire.
      These will be compared against what is actually live in Alchemer — question types, answer
      options, grid rows, scales and logic. Nothing in the survey is changed.
    </Typography>

    <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3, maxHeight: 320, overflow: 'auto' }}>
      {questions.map((q, idx) => (
        <Box
          key={idx}
          display="flex"
          alignItems="center"
          gap={1}
          px={2}
          py={1}
          sx={{ borderBottom: idx < questions.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
        >
          <Typography variant="caption" color="textSecondary" sx={{ minWidth: 36 }}>{q.id}</Typography>
          <Typography variant="body2" sx={{ flex: 1 }}>{q.title}</Typography>
          <Chip label={q.type} size="small" variant="outlined" />
        </Box>
      ))}
    </Paper>

    <Box display="flex" justifyContent="flex-end">
      <Button variant="contained" size="large" onClick={onRun} disabled={questions.length === 0}>
        Test {questions.length} question{questions.length !== 1 ? 's' : ''}
      </Button>
    </Box>
  </Box>
);

// ─── Step 3: running ─────────────────────────────────────────────────────────

const RunningStep = ({ stage, done, total }) => (
  <Box textAlign="center" py={6}>
    <CircularProgress size={48} sx={{ mb: 2 }} />
    <Typography variant="body1" fontWeight={500} mb={1}>
      {STAGE_LABELS[stage] || 'Testing survey…'}
    </Typography>
    {total > 0 && (
      <>
        <LinearProgress
          variant="determinate"
          value={(done / total) * 100}
          sx={{ maxWidth: 300, mx: 'auto', borderRadius: 1 }}
        />
        <Typography variant="body2" color="textSecondary" mt={1}>{done} / {total}</Typography>
      </>
    )}
  </Box>
);

// ─── One checklist row ───────────────────────────────────────────────────────

const FindingRow = ({ finding, ticked, onToggle, last }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(finding.expected || finding.actual);

  return (
    <Box sx={{ borderBottom: last ? 'none' : '1px solid', borderColor: 'divider' }}>
      <Box display="flex" alignItems="flex-start" gap={1} px={1.5} py={1}>
        <Checkbox
          size="small"
          checked={ticked}
          onChange={() => onToggle(finding.id)}
          sx={{ mt: -0.25 }}
        />
        <Box sx={{ mt: 0.75 }}>{SEVERITY_ICONS[finding.severity]}</Box>
        <Box
          sx={{ flex: 1, cursor: hasDetail ? 'pointer' : 'default', opacity: ticked ? 0.5 : 1 }}
          onClick={() => hasDetail && setOpen((v) => !v)}
        >
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={0.25}>
            <Chip
              label={CATEGORY_LABELS[finding.category] || finding.category}
              size="small"
              color={SEVERITY_COLOURS[finding.severity]}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ textDecoration: ticked ? 'line-through' : 'none' }}
          >
            {finding.detail}
          </Typography>
        </Box>
        {hasDetail && (
          <ExpandMoreRoundedIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              mt: 0.75,
              cursor: 'pointer',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
            onClick={() => setOpen((v) => !v)}
          />
        )}
      </Box>

      <Collapse in={open}>
        <Box px={2} pb={1.5} pl={7}>
          {finding.expected && (
            <Box mb={finding.actual ? 1 : 0}>
              <Typography variant="caption" fontWeight={700} color="success.main" display="block">
                QUESTIONNAIRE
              </Typography>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                {finding.expected}
              </Typography>
            </Box>
          )}
          {finding.actual && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="error.main" display="block">
                IN ALCHEMER
              </Typography>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                {finding.actual}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── One question's group of findings ────────────────────────────────────────

const QuestionGroup = ({ group, ticked, onToggle, onToggleGroup }) => {
  const [open, setOpen] = useState(true);
  const doneCount = group.findings.filter((f) => ticked.has(f.id)).length;
  const allDone = doneCount === group.findings.length;

  return (
    <Paper variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        px={1.5}
        py={1.25}
        sx={{
          bgcolor: allDone ? 'success.50' : 'grey.50',
          cursor: 'pointer',
          '&:hover': { bgcolor: allDone ? 'success.100' : 'grey.100' },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <Checkbox
          size="small"
          checked={allDone}
          indeterminate={doneCount > 0 && !allDone}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleGroup(group, !allDone)}
        />
        {allDone
          ? <CheckCircleRoundedIcon fontSize="small" color="success" />
          : SEVERITY_ICONS[group.worst]}
        <Typography variant="body2" color="textSecondary" fontWeight={700} sx={{ minWidth: 36 }}>
          {group.questionId || '—'}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {group.title || '(no question text)'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {doneCount}/{group.findings.length}
        </Typography>
        <ExpandMoreRoundedIcon
          fontSize="small"
          sx={{
            color: 'text.disabled',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>
      <Collapse in={open}>
        <Divider />
        {group.findings.map((finding, idx) => (
          <FindingRow
            key={finding.id}
            finding={finding}
            ticked={ticked.has(finding.id)}
            onToggle={onToggle}
            last={idx === group.findings.length - 1}
          />
        ))}
      </Collapse>
    </Paper>
  );
};

// ─── Step 4: the checklist ───────────────────────────────────────────────────

const StatTile = ({ value, label, colour }) => (
  <Paper variant="outlined" sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', minWidth: 110 }}>
    <Typography variant="h4" fontWeight={700} color={colour}>{value}</Typography>
    <Typography variant="body2" color="textSecondary">{label}</Typography>
  </Paper>
);

const ChecklistStep = ({ surveyId, results, onReset, onRerun }) => {
  const { findings, summary, warnings } = results;
  const [ticked, setTicked] = useState(() => loadTicked(surveyId));
  const [severityFilter, setSeverityFilter] = useState('all');
  const [hideDone, setHideDone] = useState(false);

  useEffect(() => saveTicked(surveyId, ticked), [surveyId, ticked]);

  const toggle = useCallback((id) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleGroup = useCallback((group, on) => {
    setTicked((prev) => {
      const next = new Set(prev);
      group.findings.forEach((f) => (on ? next.add(f.id) : next.delete(f.id)));
      return next;
    });
  }, []);

  const visible = useMemo(() => findings.filter((f) => {
    if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
    if (hideDone && ticked.has(f.id)) return false;
    return true;
  }), [findings, severityFilter, hideDone, ticked]);

  // Group by question — one Alchemer question is one unit of work.
  const groups = useMemo(() => {
    const byQuestion = new Map();
    visible.forEach((finding) => {
      const key = finding.question_id || `alchemer-${finding.alchemer_id}` || 'survey';
      if (!byQuestion.has(key)) {
        byQuestion.set(key, {
          key,
          questionId: finding.question_id,
          title: finding.title,
          findings: [],
          worst: 'info',
        });
      }
      const group = byQuestion.get(key);
      group.findings.push(finding);
      if (SEVERITY_RANK[finding.severity] < SEVERITY_RANK[group.worst]) {
        group.worst = finding.severity;
      }
    });
    return [...byQuestion.values()];
  }, [visible]);

  const doneCount = findings.filter((f) => ticked.has(f.id)).length;
  const outstanding = findings.filter((f) => !ticked.has(f.id));

  const copyOutstanding = () => {
    const lines = ['SURVEY TESTING — OUTSTANDING ITEMS', ''];
    let current = null;
    outstanding.forEach((f) => {
      const heading = `Q${f.question_id || '?'}  ${f.title || ''}`.trim();
      if (heading !== current) {
        current = heading;
        lines.push('', heading);
      }
      lines.push(`  [ ] (${f.severity}) ${CATEGORY_LABELS[f.category] || f.category}: ${f.detail}`);
      if (f.expected) lines.push(`        expected: ${f.expected}`);
      if (f.actual) lines.push(`        actual:   ${f.actual}`);
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Test Results
        </Typography>
        <Box display="flex" gap={1}>
          <Button size="small" startIcon={<ContentCopyRoundedIcon />} onClick={copyOutstanding}>
            Copy outstanding
          </Button>
          <Button size="small" variant="outlined" onClick={onRerun}>Re-test</Button>
        </Box>
      </Box>

      {findings.length === 0 ? (
        <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ mb: 2 }}>
          No problems found. All {summary.doc_questions} questions in the questionnaire match what is
          live in Alchemer, and no wording or scale issues were flagged.
        </Alert>
      ) : (
        <>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            {summary.error > 0 && <StatTile value={summary.error} label="Must fix" colour="error.main" />}
            {summary.warning > 0 && <StatTile value={summary.warning} label="Should check" colour="warning.main" />}
            {summary.info > 0 && <StatTile value={summary.info} label="For information" colour="info.main" />}
            <StatTile value={`${doneCount}/${findings.length}`} label="Ticked off" colour="success.main" />
          </Box>

          <Box mb={3}>
            <LinearProgress
              variant="determinate"
              value={findings.length ? (doneCount / findings.length) * 100 : 0}
              color="success"
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="textSecondary" mt={0.5} display="block">
              {summary.doc_questions} questions in the questionnaire · {summary.live_questions} live
              in Alchemer · tick items off as you fix them in Alchemer
            </Typography>
          </Box>
        </>
      )}

      {warnings?.map((warning, i) => (
        <Alert severity="warning" sx={{ mb: 1 }} key={i}>{warning}</Alert>
      ))}

      {findings.length > 0 && (
        <Box display="flex" alignItems="center" gap={1} mb={2} mt={2} flexWrap="wrap">
          {[
            ['all', `All (${findings.length})`],
            ['error', `Must fix (${summary.error})`],
            ['warning', `Should check (${summary.warning})`],
            ['info', `Info (${summary.info})`],
          ].map(([value, label]) => (
            <Chip
              key={value}
              label={label}
              size="small"
              onClick={() => setSeverityFilter(value)}
              color={severityFilter === value ? 'primary' : 'default'}
              variant={severityFilter === value ? 'filled' : 'outlined'}
            />
          ))}
          <Box flex={1} />
          <FormControlLabel
            control={<Checkbox size="small" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />}
            label={<Typography variant="caption">Hide ticked</Typography>}
          />
        </Box>
      )}

      {groups.map((group) => (
        <QuestionGroup
          key={group.key}
          group={group}
          ticked={ticked}
          onToggle={toggle}
          onToggleGroup={toggleGroup}
        />
      ))}

      {findings.length > 0 && groups.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Nothing left in this view — everything matching the current filter has been ticked off.
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button
          size="small"
          onClick={() => setTicked(new Set())}
          disabled={doneCount === 0}
        >
          Clear all ticks
        </Button>
        <Button variant="outlined" onClick={onReset}>Test another document</Button>
      </Box>
    </Box>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────

const SurveyTesting = () => {
  const { surveyId } = useParams();
  const [step, setStep] = useState('upload'); // upload | confirm | running | results
  const [questions, setQuestions] = useState([]);
  const [qaIssues, setQaIssues] = useState([]);
  const [progress, setProgress] = useState({ stage: 'queued', done: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const pollTimer = useRef(null);

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  const handleParsed = (qs, issues) => {
    setQuestions(qs);
    setQaIssues(issues || []);
    setStep('confirm');
  };

  const runTest = async () => {
    setProgress({ stage: 'queued', done: 0, total: 0 });
    setStep('running');
    setError(null);

    let taskId;
    try {
      const { data } = await api.post(`/surveys/${surveyId}/run-survey-test/`, { questions });
      taskId = data.task_id;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start the test. Please try again.');
      setStep('confirm');
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/surveys/${surveyId}/survey-test-status/${taskId}/`);
        if (data.state === 'success') {
          setResults(data);
          setStep('results');
        } else if (data.state === 'failure') {
          setError(data.error || 'The test failed. Please try again.');
          setStep('confirm');
        } else {
          setProgress({
            stage: data.stage || 'checking',
            done: data.done || 0,
            total: data.total || 0,
          });
          pollTimer.current = setTimeout(poll, 2000);
        }
      } catch {
        setError('Lost connection while testing. Please try again.');
        setStep('confirm');
      }
    };

    pollTimer.current = setTimeout(poll, 1500);
  };

  const reset = () => {
    clearTimeout(pollTimer.current);
    setStep('upload');
    setQuestions([]);
    setQaIssues([]);
    setResults(null);
    setError(null);
  };

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>Survey</Link>
        <Typography color="textPrimary">Survey Testing</Typography>
      </Breadcrumbs>

      <Box maxWidth={860} mx="auto">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {step === 'upload' && (
          <QnrUploadStep
            surveyId={surveyId}
            onParsed={handleParsed}
            heading="Survey Testing"
            subtitle="Upload the questionnaire this survey was built from. Claude reads it, then every question is checked against what is actually live in Alchemer — options, grid rows, scales and logic — and you get a checklist of what to fix."
          />
        )}
        {step === 'confirm' && (
          <ConfirmStep
            questions={questions}
            qaIssues={qaIssues}
            onRun={runTest}
            onReset={reset}
          />
        )}
        {step === 'running' && (
          <RunningStep stage={progress.stage} done={progress.done} total={progress.total} />
        )}
        {step === 'results' && results && (
          <ChecklistStep
            surveyId={surveyId}
            results={results}
            onReset={reset}
            onRerun={runTest}
          />
        )}
      </Box>
    </AppShell>
  );
};

export default SurveyTesting;
